import { Component, OnDestroy, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MdbModalRef } from 'mdb-angular-ui-kit/modal';
import { Subscription } from 'rxjs';
import { AppUtils } from 'src/app/core/app-utils';
import { BikePurchaseRecurringClaimDTO, CancellationReasonDTO } from 'src/app/core/models/claim';
import { ClaimService } from 'src/app/core/services/claim.service';
import { NotificationService } from 'src/app/core/services/shared-services/notification.service';
import { Messages, MessageType } from '../../app.constants';

@Component({
    selector: 'eb-cancel-bike-purchase-claim-model',
    templateUrl: './cancel-bike-purchase-claim-model.component.html',
    styleUrls: ['./cancel-bike-purchase-claim-model.component.scss'],
})
export class CancelBikePurchaseClaimModelComponent implements OnInit, OnDestroy {
    @ViewChild('errorContainer', { static: true, read: ViewContainerRef }) errorContainer: ViewContainerRef;
    title!: string;
    message!: string;
    footerNote!: string;
    isHTML = false;
    showActionButton = false;
    showCancelButton = false;
    btnText!: string;
    cancelText!: string;
    imageSrc!: string;
    componentData: BikePurchaseRecurringClaimDTO;
    componentName!: string;
    showReason: boolean = false;
    showNotes: boolean = false;
    bikePurchaseClaimForm: FormGroup;
    cancellationReasons: CancellationReasonDTO[] = [];
    cancellationReasonsSubscription: Subscription;
    cancellationReasonValueChangesSubscription: Subscription;
    cancelEnrolmentSubscription: Subscription;

    constructor(
        public modalRef: MdbModalRef<CancelBikePurchaseClaimModelComponent>,
        private claimService: ClaimService,
        private notificationService: NotificationService,
        private fb: FormBuilder
    ) {
        this.bikePurchaseClaimForm = this.fb.group({
            CancellationReasons: [null, [Validators.required]],
            Notes: ['', [Validators.required, Validators.maxLength(500)]],
        });
    }

    ngOnInit(): void {
        this.notificationService.targetElement = this.errorContainer;
        this.cancellationReasonValueChangesSubscription = this.bikePurchaseClaimForm.get('CancellationReasons').valueChanges.subscribe(value => {
            this.updateNotesValidation(value);
        });
        const idMember = Number(AppUtils.memberId);
        if (!idMember) {
            this.cancellationReasons = [];
            return;
        }

        this.cancellationReasonsSubscription = this.claimService.cancellationReasons(idMember).subscribe(response => {
            const reasons: CancellationReasonDTO[] = Array.isArray(response) ? response : (Array.isArray(response?.result) ? response.result : []);
            this.cancellationReasons = reasons;
            this.updateNotesValidation(this.bikePurchaseClaimForm.get('CancellationReasons').value);
        });
    }

    onClickAction(name: string): void {
        switch (name) {
            case 'acknowledgeContinue':
                this.acknowledgeContinue();
                break;
            case 'unenroll':
                this.unenroll();
                break;
        }
    }

    closePopup(): void {
        this.modalRef.close();
    }

    acknowledgeContinue() {
        this.showReason = true;
        this.componentName = 'unenroll';
        this.btnText = 'Unenroll';
    }

    unenroll() {
        this.bikePurchaseClaimForm.markAllAsTouched();
        if (this.bikePurchaseClaimForm.invalid) {
            this.notificationService.targetElement = this.errorContainer;
            this.notificationService.displayMessage('Please enter valid fields.', MessageType.Error);
            return;
        }

        const idMember = Number(AppUtils.memberId);
        const idCRMRecurringClaim = this.componentData?.idCrmRecurringClaim;
        const cancelData = {
            idClaimCancellationReason: this.bikePurchaseClaimForm.get('CancellationReasons').value,
            cancellationReasonOther: this.bikePurchaseClaimForm.get('Notes').value,
        };

        if (!idMember || !idCRMRecurringClaim) {
            this.notificationService.displayMessage(Messages.apiError, MessageType.Error);
            return;
        }

        this.cancelEnrolmentSubscription = this.claimService
            .cancelEnrolment(idMember, idCRMRecurringClaim, cancelData)
            .subscribe((response: boolean) => {
                if (response) {
                    this.notificationService.displayMessage('You’ve successfully unenrolled from the bike purchase program.', MessageType.Success);
                    setTimeout(() => {
                        this.modalRef.close(true);
                    }, 2000);
                } else {
                    this.notificationService.displayMessage(Messages.apiError, MessageType.Error);
                }
            });
    }

    private updateNotesValidation(selectedCancellationReasonId: number): void {
        const selectedReason = this.cancellationReasons.find(
            reason => reason.idClaimCancellationReason === selectedCancellationReasonId
        );
        const isOtherSelected =
            (selectedReason?.cancellationReasonCode || '').toLowerCase() === 'other' ||
            (selectedReason?.cancellationReasonDescription || '').toLowerCase() === 'other';

        this.showNotes = isOtherSelected;
        const notesControl = this.bikePurchaseClaimForm.get('Notes');
        if (!notesControl) {
            return;
        }

        if (isOtherSelected) {
            notesControl.setValidators([Validators.required, Validators.maxLength(500)]);
        } else {
            notesControl.clearValidators();
            notesControl.setValue('');
            notesControl.markAsPristine();
            notesControl.markAsUntouched();
        }
        notesControl.updateValueAndValidity();
    }

    trackByCancellationReason(index: number, reason: CancellationReasonDTO): number {
        return reason.idClaimCancellationReason;
    }

    ngOnDestroy(): void {
        if (this.cancellationReasonsSubscription) {
            this.cancellationReasonsSubscription.unsubscribe();
        }
        if (this.cancellationReasonValueChangesSubscription) {
            this.cancellationReasonValueChangesSubscription.unsubscribe();
        }
        if (this.cancelEnrolmentSubscription) {
            this.cancelEnrolmentSubscription.unsubscribe();
        }
    }
}
